import { useState, useEffect } from 'react';

export interface LessonProgress {
  lessonId: string;
  watchTime: number;
  totalDuration: number;
  percentage: number;
  completed: boolean;
  lastWatchedAt: number;
}

export interface ModuleProgress {
  moduleId: string;
  lessons: Record<string, LessonProgress>;
  lastLessonId?: string;
  lastUpdated: number;
}

interface DarkMembersProgress {
  modules: Record<string, ModuleProgress>;
  version: string;
}

const STORAGE_KEY = 'dm_progress_v1';

export function useDarkMembersProgress(moduleId: string) {
  const [progress, setProgress] = useState<ModuleProgress>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data: DarkMembersProgress = JSON.parse(stored);
        return data.modules[moduleId] || {
          moduleId,
          lessons: {},
          lastUpdated: Date.now(),
        };
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    }
    return {
      moduleId,
      lessons: {},
      lastUpdated: Date.now(),
    };
  });

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    let allProgress: DarkMembersProgress = {
      modules: {},
      version: '1.0',
    };

    if (stored) {
      try {
        allProgress = JSON.parse(stored);
      } catch (error) {
        console.error('Error parsing progress:', error);
      }
    }

    allProgress.modules[moduleId] = progress;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress));
  }, [progress, moduleId]);

  const updateLessonProgress = (
    lessonId: string,
    watchTime: number,
    totalDuration: number
  ) => {
    const percentage = Math.min(100, Math.round((watchTime / totalDuration) * 100));
    const completed = percentage >= 95; // Consider complete if 95%+ watched

    setProgress((prev) => ({
      ...prev,
      lessons: {
        ...prev.lessons,
        [lessonId]: {
          lessonId,
          watchTime,
          totalDuration,
          percentage,
          completed,
          lastWatchedAt: Date.now(),
        },
      },
      lastLessonId: lessonId,
      lastUpdated: Date.now(),
    }));
  };

  const markLessonComplete = (lessonId: string, totalDuration: number) => {
    setProgress((prev) => ({
      ...prev,
      lessons: {
        ...prev.lessons,
        [lessonId]: {
          lessonId,
          watchTime: totalDuration,
          totalDuration,
          percentage: 100,
          completed: true,
          lastWatchedAt: Date.now(),
        },
      },
      lastLessonId: lessonId,
      lastUpdated: Date.now(),
    }));
  };

  const getLessonProgress = (lessonId: string): LessonProgress | null => {
    return progress.lessons[lessonId] || null;
  };

  const getLastLesson = (): { lessonId: string; watchTime: number } | null => {
    if (!progress.lastLessonId) return null;

    const lastLesson = progress.lessons[progress.lastLessonId];
    if (!lastLesson) return null;

    return {
      lessonId: progress.lastLessonId,
      watchTime: lastLesson.watchTime,
    };
  };

  const getAllLessonsProgress = (): Record<string, LessonProgress> => {
    return progress.lessons;
  };

  return {
    updateLessonProgress,
    markLessonComplete,
    getLessonProgress,
    getLastLesson,
    getAllLessonsProgress,
  };
}
